import { RightOutlined } from "@ant-design/icons"
import { Button, Flex, Skeleton, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import { GLOBAL_MAP_SURFACE_CLASS } from "./global-map-surface.ts"
import { relativeTime } from "./helpers/relative-time.ts"
import { useRecentActivityInView } from "./hooks/useRecentActivityInView.ts"
import { EntityGlyphTile } from "./map/EntityGlyphTile.tsx"
import type {
  AnalyticsRecentActivityEvent,
  EntityType
} from "./redux/global-map.types"

const { Title, Text } = Typography

// A row hovers toward the entity it is about — the same Lead-blue/
// Project-green grammar the markers, the legend and the layer chips use.
// Whole class strings because Tailwind only sees literals.
const ROW_HOVER_CLASS: Record<EntityType, string> = {
  lead: "hover:border-blue-300 hover:bg-blue-50/50",
  project: "hover:border-green-300 hover:bg-green-50/50"
}

interface RecentActivityInViewProps {
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
  onViewAll: () => void
}

/**
 * "Recent Activity in View" — beneath the map. Reuses the light dedicated
 * `compute_recent_activity_by_entity` endpoint (not the full Analytics
 * computation — the Map tab is this app's highest-traffic surface), scoped to
 * the SAME shared filters/search/layers every other Map-tab surface reads.
 *
 * ONE chronological feed, not a Lead column beside a Project column. The
 * split was why this card became unreadable once it moved into a third of the
 * row: each half got roughly a sixth of the page, so a record rendered as
 * "Co…" and its timestamp as "1m…". The record's name is the content that
 * matters here, so it gets the row's full width and the entity is carried by
 * its own coloured tile rather than by a column heading.
 *
 * Merging the two lists by time cannot let a busy entity crowd the other out
 * of the response — the backend still guarantees each type its own latest
 * events (`_recent_activity_events_for_entity`) — only out of the visible
 * top few, which is what "recent" means.
 */
const RecentActivityInView = ({
  onSelectEvent,
  onViewAll
}: RecentActivityInViewProps) => {
  const { t } = useTranslation()
  const { data, status, error, refetch } = useRecentActivityInView()

  const events = useMemo(() => {
    if (!data) return []
    return [...data.lead, ...data.project].sort(
      (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
    )
  }, [data])

  const isLoading = status === "loading"
  const isError = status === "error"
  const isEmpty = status === "idle" && data !== null && events.length === 0

  return (
    <div
      className={`flex min-w-0 flex-col gap-3 p-4 ${GLOBAL_MAP_SURFACE_CLASS}`}
      data-testid="global-map-recent-activity"
    >
      <Flex justify="space-between" align="center" gap={8}>
        <Title
          level={5}
          className="!mb-0 min-w-0 truncate !text-[15px] font-semibold !text-[#0F172A]"
        >
          {t("globalMap.recentActivity.title")}
        </Title>
        <button
          type="button"
          onClick={onViewAll}
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold text-primary hover:underline"
        >
          {t("globalMap.recentActivity.viewAll")}
        </button>
      </Flex>

      {isLoading && <Skeleton active paragraph={{ rows: 3 }} title={false} />}

      {isError && (
        <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 py-6">
          <Text type="secondary" className="text-xs">
            {t("globalMap.recentActivity.loadError")}
          </Text>
          {error?.code && (
            <Text type="secondary" className="text-[11px]">
              {error.code}
            </Text>
          )}
          <Button size="small" onClick={refetch} className="mt-1">
            {t("globalMap.recentActivity.retry")}
          </Button>
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-1 items-center justify-center gap-3 rounded-lg border border-dashed border-[#CBD5E1] bg-slate-50/40 py-8">
          <span
            aria-hidden
            className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-[18px] text-blue-500"
          >
            <Icon type="Files" />
          </span>
          <Text className="text-[13px] text-slate-500">
            {t("globalMap.recentActivity.empty")}
          </Text>
        </div>
      )}

      {!isLoading && !isError && !isEmpty && (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {events.map((event, index) => (
            <li key={`${event.recordId}-${event.timestamp}-${index}`}>
              <button
                type="button"
                onClick={() => onSelectEvent(event)}
                className={`flex w-full items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-2.5 text-left transition-colors ${ROW_HOVER_CLASS[event.entityType]}`}
              >
                <EntityGlyphTile entityType={event.entityType} size={32} />
                {/* `min-w-0` is what lets a long record name ellipsize inside
                    the row rather than forcing the row wider than its card. */}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[11px] font-medium text-slate-400">
                    {event.description}
                  </span>
                  <span className="truncate text-[14px] font-semibold leading-snug text-slate-900">
                    {event.recordTitle}
                  </span>
                  <span className="truncate text-[11px] text-slate-400">
                    {relativeTime(event.timestamp)}
                  </span>
                </span>
                <RightOutlined
                  aria-hidden
                  className="shrink-0 !text-[11px] text-slate-300"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default RecentActivityInView
